import React from 'react';
import { Typography, Box, Chip, IconButton, Tooltip, LinearProgress, Divider } from '@mui/material';
import {
  FaCalendarAlt,
  FaUsers,
  FaEye,
  FaEdit,
  FaTrash,
  FaUserEdit,
  FaCog,
  FaPlay,
  FaCheckCircle,
  FaStopCircle,
  FaChartBar,
  FaProjectDiagram,
  FaInfoCircle,
  FaFileAlt
} from 'react-icons/fa';
import { ReviewTypeBadge } from '../../common/ReviewTypeBadge';
import {
  SessionCard,
  SessionCardContent,
  SessionTitle,
  BusinessUnitName,
  SessionDetails,
  DetailRow,
  StatusChip,
} from '../styles';
import {
  getSessionStatus,
  FEEDBACK_SESSION_STATUSES
} from '../../../constants/feedbackSessionStatuses';
import { SessionStatusChip } from '../../common/SessionStatusChip';
import {
  ASSIGNMENT_STATUSES,
  getAssignmentStatus,
  getStatusBorderColor as getAssignmentStatusBorderColor,
  getReviewerStatus
} from '../../../constants/assignmentStatuses';
import {
  REVIEW_TYPES,
  getReviewTypeColor,
  getReviewTypeTextColor,
  getReviewTypeText
} from '../../../constants/reviewTypes';
import { getDurationFromNow, getRelativeTime } from '../../../utils/dateUtils';
import { canDeleteSession } from '../../../utils/feedbackSessionDeletion';
import { toLocalDate } from '../../../utils/dateOnly';

const tooltipConfig = {
  enterDelay: 100,
};

// Component for session progress bars
const SessionProgress = ({
  assignments,
  statusBorderColor,
  completedStatusBorderColor,
  getCompletedCount,
  getInProgressCount,
  completedText = "completed",
  inProgressText = "in progress",
  showOnlyCompleted = false,
  header,
  tooltip
}) => {
  const completedCount = getCompletedCount(assignments);
  const inProgressCount = getInProgressCount(assignments);
  const totalCount = assignments.length;
  const completedPercentage = (completedCount / totalCount) * 100 || 0;
  const inProgressPercentage = ((completedCount + inProgressCount) / totalCount) * 100 || 0;

  return (
    <>
      {header && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0 }}>
          <Typography sx={{
            fontSize: '13px',
            fontWeight: 600,
            color: '#64748B',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {header}
          </Typography>
          {tooltip && (
            <Tooltip
              title={tooltip}
              {...tooltipConfig}
            >
              <FaInfoCircle
                size={12}
                style={{
                  color: '#64748B',
                  cursor: 'help'
                }}
              />
            </Tooltip>
          )}
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: '14px' }}>
          {completedCount} of {totalCount} {completedText}
        </Typography>
        <Typography sx={{ fontSize: '14px', color: statusBorderColor }}>
          {inProgressCount} {inProgressText}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', mb: 1 }}>
        <Box sx={{ position: 'relative', width: '100%', height: 8 }}>
          {!showOnlyCompleted && (
            <LinearProgress
              variant="determinate"
              value={inProgressPercentage}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: '#E2E8F0',
                width: '100%',
                position: 'absolute',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: statusBorderColor,
                  borderRadius: 4,
                }
              }}
            />
          )}
          <LinearProgress
            variant="determinate"
            value={completedPercentage}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: showOnlyCompleted ? '#E2E8F0' : 'transparent',
              width: '100%',
              position: 'absolute',
              '& .MuiLinearProgress-bar': {
                backgroundColor: showOnlyCompleted ? statusBorderColor : completedStatusBorderColor,
                borderRadius: 4,
              }
            }}
          />
        </Box>
        <Typography
          sx={{
            fontSize: '12px',
            color: '#64748B',
            fontStyle: 'italic',
            minWidth: '40px',
            textAlign: 'right'
          }}
        >
          {Math.round(completedPercentage)}%
        </Typography>
      </Box>
    </>
  );
};

const SessionCardItem = ({
  session,
  onView,
  onEdit,
  onEditEmployees,
  onEditReviewers,
  onDelete,
  onPublishConfigure,
  onPublishStart,
  onPublishComplete,
  onCloseWithIncomplete,
  onViewReport,
  ability
}) => {
  const status = getSessionStatus(session);
  // ADR-014 §10o: drives BOTH terminal actions — Complete is offered only when
  // this is false, Close with Unfinished Work only when it is true.
  //
  // CAUTION: this matches `closeWithIncomplete` (which derives the same way via
  // buildClosureSummary) but NOT `publishToCompleted`, whose gate filters on the
  // raw `!assignment.completedAt`. An assignment with feedbackAttachedAt +
  // reviewsCompletedAt but no completedAt therefore reads complete here and
  // incomplete there — see the deferred item in ADR-014. Do not "simplify" this
  // to `!assignment.completedAt` without changing the close side too, or the
  // session becomes terminable by neither action.
  const hasIncompleteAssignments = (session.assignments || []).some(
    (assignment) => getAssignmentStatus(assignment) !== ASSIGNMENT_STATUSES.COMPLETED
  );

  const handleClick = (handler, e) => {
    if (e) {
      e.stopPropagation();
    }
    handler(session, e);
  };

  return (
    <SessionCard>
      <StatusChip>
        {/* ADR-014 §10: ONE chip carries both the lifecycle state and how
            the session was closed — see common/SessionStatusChip. */}
        <SessionStatusChip session={session} />
      </StatusChip>
      <SessionCardContent>
        <SessionTitle>
          {session.name}
        </SessionTitle>
        <BusinessUnitName>
          {session.businessUnit.name}
        </BusinessUnitName>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          mb: 1.5,
          fontSize: '13px',
          color: '#64748B',
        }}>
          <DetailRow>
            <FaProjectDiagram />
            {session.sessionProjects && session.sessionProjects.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {session.sessionProjects.map(project => (
                  <Chip
                    key={project.id}
                    label={project.name}
                    size="small"
                    sx={{
                      fontSize: '12px',
                      height: '22px',
                      backgroundColor: '#F8FAFC',
                      color: '#64748B',
                      border: '1px solid #E2E8F0',
                    }}
                  />
                ))}
              </Box>
            ) : (
              <Typography sx={{
                fontSize: '12px',
                fontStyle: 'italic',
                color: '#64748B'
              }}>
                All projects included
              </Typography>
            )}
          </DetailRow>
        </Box>
        <Box sx={{
          mb: 1,
          width: 'fit-content',
          display: 'block'
        }}>
          <ReviewTypeBadge
            reviewType={session.reviewType}
            getReviewTypeColor={getReviewTypeColor}
            getReviewTypeTextColor={getReviewTypeTextColor}
            getReviewTypeText={getReviewTypeText}
          />
        </Box>
        {session.sessionTemplate && (
          <Box sx={{
            mb: 1.5,
            fontSize: '13px',
            color: '#64748B',
          }}>
            <DetailRow>
              <FaFileAlt size={14} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '14px' }}>
                  {session.sessionTemplate.name}
                </Typography>
                {session.sessionTemplate.version && (
                  <Chip
                    label={`v${session.sessionTemplate.version}`}
                    size="small"
                    sx={{
                      fontSize: '11px',
                      height: '20px',
                      backgroundColor: '#F1F5F9',
                      color: '#475569',
                      border: '1px solid #E2E8F0',
                      fontWeight: 500,
                    }}
                  />
                )}
              </Box>
            </DetailRow>
          </Box>
        )}
        <SessionDetails>
          {status === FEEDBACK_SESSION_STATUSES.COMPLETED ? (
            <DetailRow>
              <FaCheckCircle size={14} />
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography sx={{ fontSize: '14px' }}>Completed: {new Date(session.completedAt).toLocaleDateString()}</Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: '#64748B',
                    fontStyle: 'italic'
                  }}
                >
                  {getRelativeTime(session.completedAt)} ago
                </Typography>
              </Box>
            </DetailRow>
          ) : (
            <DetailRow>
              <FaCalendarAlt size={14} />
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography sx={{ fontSize: '14px' }}>Deadline: {toLocalDate(session.deadlineAt)?.toLocaleDateString()}</Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: '#64748B',
                    fontStyle: 'italic'
                  }}
                >
                  {getDurationFromNow(session.deadlineAt)}
                </Typography>
              </Box>
            </DetailRow>
          )}
          <DetailRow>
            <FaUsers size={14} />
            {session.assignments.length} {session.assignments.length === 1 ? 'individual attached' : 'individuals attached'}
          </DetailRow>
          {session.allowEmployeeToAssignReviewers && (
            <DetailRow>
              <FaUserEdit size={14} />
              <Typography
                sx={{
                  fontSize: '12px',
                  color: '#64748B',
                  fontStyle: 'italic',
                }}
              >
                Individuals are allowed to assign reviewers
              </Typography>
            </DetailRow>
          )}
          {(status === FEEDBACK_SESSION_STATUSES.IN_PROGRESS
            || status === FEEDBACK_SESSION_STATUSES.COMPLETED) && (
            <DetailRow>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                {session.assignments.length > 0 ? (
                  <>
                  <Divider sx={{ mb: 3, mt: 0, borderColor: '#E2E8F0' }} />
                  <SessionProgress
                    assignments={session.assignments}
                    statusBorderColor={getAssignmentStatusBorderColor(ASSIGNMENT_STATUSES.IN_PROGRESS)}
                    completedStatusBorderColor={getAssignmentStatusBorderColor(ASSIGNMENT_STATUSES.COMPLETED)}
                    getCompletedCount={(assignments) => assignments.filter(assignment => getAssignmentStatus(assignment) === ASSIGNMENT_STATUSES.COMPLETED).length}
                    getInProgressCount={(assignments) => assignments.filter(assignment => [ASSIGNMENT_STATUSES.IN_PROGRESS, ASSIGNMENT_STATUSES.PENDING_ACTIONS].includes(getAssignmentStatus(assignment))).length}
                    completedText="completed"
                    inProgressText="in progress"
                    header="Review Progress"
                    tooltip="Shows the number of individuals for whom all reviewers have completed their feedback.'In progress' indicates that at least one reviewer has started an individual assignment, but not all are done."
                  />
                  <SessionProgress
                    assignments={session.assignments}
                    statusBorderColor={getAssignmentStatusBorderColor(ASSIGNMENT_STATUSES.PENDING_ACTIONS)}
                    completedStatusBorderColor={getAssignmentStatusBorderColor(ASSIGNMENT_STATUSES.COMPLETED)}
                    getCompletedCount={(assignments) => assignments.filter(assignment => getAssignmentStatus(assignment) === ASSIGNMENT_STATUSES.COMPLETED).length}
                    getInProgressCount={(assignments) => {
                      // For sequential sessions, check if the last reviewer has PENDING_ACTIONS status
                      if (session.reviewType === REVIEW_TYPES.SEQUENTIAL) {
                        return assignments.filter(assignment => {
                          // Find the last reviewer (highest order) for this assignment
                          const sequentialReviewers = assignment.reviewers?.filter(r => r.reviewType === REVIEW_TYPES.SEQUENTIAL) || [];
                          if (sequentialReviewers.length === 0) {
                            return false;
                          }

                          const lastReviewer = sequentialReviewers.reduce((max, reviewer) =>
                            reviewer.order > (max.order || 0) ? reviewer : max
                          );

                          // Use the existing getReviewerStatus function to check for PENDING_ACTIONS
                          return getReviewerStatus(lastReviewer) === ASSIGNMENT_STATUSES.PENDING_ACTIONS;
                        }).length;
                      }

                      // For non-sequential, use the original logic
                      return assignments.filter(assignment => [ASSIGNMENT_STATUSES.PENDING_ACTIONS].includes(getAssignmentStatus(assignment))).length;
                    }}
                    completedText="actions provided"
                    inProgressText="pending actions"
                    showOnlyCompleted={true}
                    header="Post-Review Actions"
                    tooltip="Tracks how many follow-up START/STOP/CONTINUE actions have been provided by the Representative after all reviews are completed. 'Pending actions' appear once reviews are fully submitted for an individual."
                  />
                  </>
                ) : (
                  <Typography sx={{ fontSize: '14px', color: '#64748B', fontStyle: 'italic' }}>
                    No assignments added yet
                  </Typography>
                )}
              </Box>
            </DetailRow>
          )}
        </SessionDetails>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 3,
          pt: 3,
          borderTop: '1px solid #E2E8F0',
          gap: 1
        }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {status === FEEDBACK_SESSION_STATUSES.NOT_STARTED && ability?.can('publish', 'FeedbackSessionInstance') && (
              <Tooltip
                title="Publish to Configuration State - Makes the session available for configuration"
                {...tooltipConfig}
              >
                <IconButton
                  size="small"
                  onClick={(e) => handleClick(onPublishConfigure, e)}
                  sx={{
                    backgroundColor: '#FEF9C3',
                    border: '1px solid #FDE047',
                    '&:hover': {
                      backgroundColor: '#FEF08A',
                    }
                  }}
                >
                  <FaCog size={14} color="#CA8A04" />
                </IconButton>
              </Tooltip>
            )}
            {status === FEEDBACK_SESSION_STATUSES.CONFIGURATION && ability?.can('publish', 'FeedbackSessionInstance') && (
              <Tooltip
                title="Start Session - Makes the session available to all assigned individuals and reviewers"
                {...tooltipConfig}
              >
                <IconButton
                  size="small"
                  onClick={(e) => handleClick(onPublishStart, e)}
                  sx={{
                    backgroundColor: '#E0F2FE',
                    border: '1px solid #7DD3FC',
                    '&:hover': {
                      backgroundColor: '#BAE6FD',
                    }
                  }}
                >
                  <FaPlay size={14} color="#0284C7" />
                </IconButton>
              </Tooltip>
            )}
            {/* ADR-014 §10o: exactly one terminal action is ever valid, so only
                one is ever offered — Complete when everything is done, Close with
                Unfinished Work otherwise. Without the completeness check this
                button was offered in a state where the API can only answer 400
                ("there are still incomplete assignments"). */}
            {status === FEEDBACK_SESSION_STATUSES.IN_PROGRESS
              && !hasIncompleteAssignments
              && ability?.can('publish', 'FeedbackSessionInstance') && (
              <Tooltip
                title="Complete Session - Finalizes the session and makes it read-only"
                {...tooltipConfig}
              >
                <IconButton
                  size="small"
                  onClick={(e) => handleClick(onPublishComplete, e)}
                  sx={{
                    backgroundColor: '#DCFCE7',
                    border: '1px solid #86EFAC',
                    '&:hover': {
                      backgroundColor: '#BBF7D0',
                    }
                  }}
                >
                  <FaCheckCircle size={14} color="#16A34A" />
                </IconButton>
              </Tooltip>
            )}
            {/* ADR-014: snapshot close — only for holders of the dedicated
                FeedbackSessionClosure permission (§9); irreversible, so the
                confirmation dialog lists the open work and requires a reason. */}
            {status === FEEDBACK_SESSION_STATUSES.IN_PROGRESS
              && hasIncompleteAssignments
              && onCloseWithIncomplete
              && ability?.can('manage', 'FeedbackSessionClosure') && (
              <Tooltip
                title="Close with Unfinished Work - Ends the session now; incomplete assignments are preserved as-is and can never be finished"
                {...tooltipConfig}
              >
                <IconButton
                  size="small"
                  onClick={(e) => handleClick(onCloseWithIncomplete, e)}
                  sx={{
                    backgroundColor: '#FEF3C7',
                    border: '1px solid #FCD34D',
                    '&:hover': {
                      backgroundColor: '#FDE68A',
                    }
                  }}
                >
                  <FaStopCircle size={14} color="#D97706" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip
              title="View session details"
              {...tooltipConfig}
            >
              <IconButton
                size="small"
                onClick={(e) => handleClick(onView, e)}
                sx={{
                  backgroundColor: '#F0FDF4',
                  border: '1px solid #BBF7D0',
                  '&:hover': {
                    backgroundColor: '#DCFCE7',
                  }
                }}
              >
                <FaEye size={14} color="#22C55E" />
              </IconButton>
            </Tooltip>
            {(status === FEEDBACK_SESSION_STATUSES.IN_PROGRESS || status === FEEDBACK_SESSION_STATUSES.COMPLETED) && (
              <Tooltip
                title="View session report"
                {...tooltipConfig}
              >
                <IconButton
                  size="small"
                  onClick={(e) => handleClick(onViewReport, e)}
                  sx={{
                    backgroundColor: '#F0F7FF',
                    border: '1px solid #BFDBFE',
                    '&:hover': {
                      backgroundColor: '#DBEAFE',
                    }
                  }}
                >
                  <FaChartBar size={14} color="#3B82F6" />
                </IconButton>
              </Tooltip>
            )}
            {
            ((
              (status === FEEDBACK_SESSION_STATUSES.NOT_STARTED
              || status === FEEDBACK_SESSION_STATUSES.CONFIGURATION)
              && ability?.can('update', 'FeedbackSessionInstance')
            ) || ability?.can('update', 'FeedbackSessionInstanceOverride')
           ) && (
              <>
                <Tooltip
                  title="Edit individuals and reviewers"
                  {...tooltipConfig}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => handleClick(onEditEmployees, e)}
                    sx={{
                      backgroundColor: '#F0F9FF',
                      border: '1px solid #BAE6FD',
                      '&:hover': {
                        backgroundColor: '#E0F2FE',
                      }
                    }}
                  >
                    <FaUsers size={14} color="#0EA5E9" />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title="Edit reviewers"
                  {...tooltipConfig}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => handleClick(onEditReviewers, e)}
                    sx={{
                      backgroundColor: '#F0F7FF',
                      border: '1px solid #BFDBFE',
                      '&:hover': {
                        backgroundColor: '#DBEAFE',
                      }
                    }}
                  >
                    <FaUserEdit size={14} color="#3B82F6" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {status === FEEDBACK_SESSION_STATUSES.NOT_STARTED && ability?.can('manage', 'FeedbackSessionInstance') && (
              <Tooltip
                title="Edit session"
                {...tooltipConfig}
              >
                <IconButton
                  size="small"
                  onClick={(e) => handleClick(onEdit, e)}
                  sx={{
                    backgroundColor: '#F8FAFC',
                    border: '1px solid #E2E8F0',
                    '&:hover': {
                      backgroundColor: '#F1F5F9',
                    }
                  }}
                >
                  <FaEdit size={14} color="#475569" />
                </IconButton>
              </Tooltip>
            )}
            {/* ADR-192/193: Delete is offered iff the API says so — the viewer
                may delete sessions in its cluster (any status, with or without
                individuals; the dialog shows what will be deleted). */}
            {canDeleteSession(session) && (
              <Tooltip
                title="Delete session"
                {...tooltipConfig}
              >
                <IconButton
                  size="small"
                  onClick={(e) => handleClick(onDelete, e)}
                  sx={{
                    backgroundColor: '#FEF2F2',
                    border: '1px solid #FECACA',
                    '&:hover': {
                      backgroundColor: '#FEE2E2',
                    }
                  }}
                >
                  <FaTrash size={14} color="#EF4444" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </SessionCardContent>
    </SessionCard>
  );
};

export default SessionCardItem;
